import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map, of, retry, tap, timer, catchError, combineLatest, timeout, throwError, EMPTY } from 'rxjs';
import { format } from 'date-fns';
import { Utilities } from '@/shared/utilities/utilities';
import { environment } from '../../../../../environments/environment';
import { NavigationService, SESSION_KEY } from '@/shared/services';
import { SessionService } from '@/shared/services/session.service';
import { ACCOUNT_STATUS } from '@/shared/interfaces/gcp/accountStatus.enum';
import { DEVICE_ISSUES } from '../../devices/device-issue-constants';
import { v4 as uuidv4 } from 'uuid';
import {
  ACCOUNT_STATUS_ACTION_CODES,
  BROADBAND_HUB_TYPES,
  CATEGORY_MAPPINGS,
  ERROR_ACTION_CODES,
  TV_BOX_TYPES,
  TV_BOX_TYPES_BY_PRODUCT_CODE,
} from './diagnostic-check.constants';
import { BLACK_PODS, WHITE_PODS } from '../../pod-health/pod-health-issue-constants';

@Injectable({
  providedIn: 'root',
})
export class DiagnosticCheckService {
  private sessionService: SessionService = inject(SessionService);
  private navigationService: NavigationService = inject(NavigationService);
  private http: HttpClient = inject(HttpClient);

  constructor() {
    this.initializeFeatureFlags();
  }

  private initializeFeatureFlags(): void {
    const featureFlags = {
      ...environment.featureFlags,
    };
    this.sessionService.setData(SESSION_KEY.FEATURE_FLAGS, featureFlags);
  }

  isFeatureEnabled(featureKey: keyof typeof environment.featureFlags): boolean {
    const sessionFlags = this.sessionService.getData(SESSION_KEY.FEATURE_FLAGS);
    return sessionFlags?.[featureKey] ?? environment.featureFlags[featureKey];
  }

  getAccountCheck(): Observable<any> {
    return this.http.get(`${environment.gcp.baseUrl}${environment.gcp.diagnostics?.accountCheck}`, {}).pipe(
      map((res: any) => {
        if (res?.equipmentAndProductDetails?.equipment?.length > 0) {
          res?.equipmentAndProductDetails?.equipment?.forEach((eq) => {
            if (eq.productType == 'BB') {
              eq.deviceType = this.getBBType(eq.productName);
            } else if (eq.productType == 'TV') {
              eq.deviceType = this.getTVTypeByProductCode(eq.productCode);
            }
          });
        }

        if (res?.accountCheck) {
          res.accountStatus = res?.accountCheck;
        }

        return res;
      }),
      tap((res) => {
        this.sessionService.setData(SESSION_KEY.ACCOUNT_CHECK, res);
      })
    );
  }

  getBBType(productName: string): string {
    let result = BROADBAND_HUB_TYPES[0];

    productName = productName?.toLocaleLowerCase().replace(/\s+/g, '');

    if (productName === BROADBAND_HUB_TYPES[4]) {
      // Hub5x should be an exact match
      return productName;
    }

    for (const deviceType of BROADBAND_HUB_TYPES) {
      if (productName?.includes(deviceType)) {
        result = deviceType;
        break;
      }
    }

    return result;
  }

  getTVType(modalName: string): string {
    let result = '';

    modalName = modalName?.toLocaleLowerCase().replace(/\s+/g, '');

    for (const deviceType of TV_BOX_TYPES) {
      if (modalName?.includes(deviceType)) {
        result = deviceType;
        break;
      }
    }

    return result;
  }

  getTVTypeByProductCode(productCode: string): string {
    let result = '';

    const tvBox = TV_BOX_TYPES_BY_PRODUCT_CODE?.filter((item) => item.productCode === productCode)?.map(
      (item) => item?.tvType
    );

    if (tvBox?.length == 1) {
      result = tvBox?.[0];
    }

    return result;
  }

  getDiagnosticCheck(ivrToken: string | null = null, isMultiChannel = false, isPodCheck = false): Observable<any> {
    const ses = this.sessionService.getData(SESSION_KEY.ACCOUNT_CHECK);

    let productTypeBB$: Observable<any> = of(null);
    let productTypeTV$: Observable<any> = of(null);
    let headers = new HttpHeaders();

    if (ivrToken) {
      headers = headers.set('DAPI-deflection', ivrToken);
    }

    if (isMultiChannel) {
      headers = headers.set('DAPI-multichannel', 'enabled');
    }

    if (ses?.equipmentAndProductDetails?.productSet?.includes('BB')) {
      productTypeBB$ = this.getDiagnosticRequest('BB', headers).pipe(
        catchError((res: Error) => {
          if (isPodCheck) {
            this.navigationService.toUrl('/status/pod-health/error');
            return EMPTY;
          }
          if (res?.message.includes('DC_timed_out')) {
            this.navigationService.toApiError('408', 'DC_timed_out');
            return EMPTY;
          }

          return of({ productType: 'BB', actionCodes: [{ actionCode: 'ERR' }] });
        })
      );
    }

    if (environment.featureFlags.tvDiagnosticsEnabled && ses?.equipmentAndProductDetails?.productSet?.includes('TV')) {
      productTypeTV$ = this.getDiagnosticRequest('TV', headers).pipe(
        catchError((res: Error) => {
          if (res?.message.includes('DC_timed_out')) {
            this.navigationService.toApiError('408', 'DC_timed_out');
            return EMPTY;
          }
          return of({ productType: 'TV', actionCodes: [{ actionCode: 'ERR' }] });
        })
      );
    }

    return combineLatest({ productTypeBB$, productTypeTV$ }).pipe(
      map((response: any) => {
        const res: any[] = [];
        response.productTypeBB$ ? res.push(response.productTypeBB$) : null;
        response.productTypeTV$ ? res.push(response.productTypeTV$) : null;

        if (!res[0]?.body) {
          return [res[1], res[0]];
        }

        return res;
      }),
      map((res: any) => {
        if (res.length === 1) {
          return res[0];
        }

        for (let i = 1; i < res.length; i++) {
          if (res[i]?.body) {
            res[i].body?.deviceDetails?.forEach((dd) => {
              res[0].body?.deviceDetails?.push(dd);
            });
            res[i].body?.generalDiagnosticDataByDevice?.forEach((gd) => {
              res[0].body?.generalDiagnosticDataByDevice?.push(gd);
            });
          } else {
            res[0].body?.deviceDetails?.push(res[i]);
            res[0].body?.generalDiagnosticDataByDevice?.push(res[i]);
          }
        }

        return res[0];
      })
    );
  }

  getCustomerInfo(include = 'all'): Observable<any> {
    const apiURL = `${environment?.gcp?.baseUrl}${environment?.gcp?.customerInfo}?include=${include}`;
    return this.http.get(apiURL).pipe(
      map((ci: any) => {
        // order remote
        if (ci?.lastRemoteOrderDate) {
          ci.lastRemoreOrderDateFormatted = format(
            new Date(new Date(ci?.lastRemoteOrderDate).getTime() + 2 * 24 * 60 * 60 * 1000),
            'EEEE do MMMM'
          );
        }

        // pod recommender
        ci.podOrderDateInLast5Days = false;
        ci.podOrderDateInLast30Days = false;

        if (ci?.lastPodOrderDate) {
          const numDaysSinceLastOrder = this.getDaysSince(ci?.lastPodOrderDate);
          ci.podOrderArrivalDatePretty = new Date(ci?.lastPodOrderDate);
          ci.podOrderArrivalDatePretty = ci?.podOrderArrivalDatePretty.setDate(
            ci?.podOrderArrivalDatePretty.getDate() + 5
          );
          ci.podOrderArrivalDatePretty = format(ci?.podOrderArrivalDatePretty, 'EEEE do MMMM');
          const lastPodOrderDate = new Date(ci?.lastPodOrderDate).getTime() + 7 * 24 * 60 * 60 * 800;
          ci.lastPrettyPodOrderDate = format(lastPodOrderDate, 'EEEE do MMMM');
          ci.podOrderDateInLast5Days = numDaysSinceLastOrder <= 5;
          ci.podOrderDateInLast30Days = numDaysSinceLastOrder <= 30;
        }

        // hub swap
        if (ci?.lastHubSwapUpgradeDate) {
          const numDaysSinceLastOrder = this.getDaysSince(ci?.lastHubSwapUpgradeDate);
          ci.lastHubSwapDaysSince = numDaysSinceLastOrder;
          ci.hubSwapUpgradeDateInLast5Days = numDaysSinceLastOrder <= 5;
          ci.hubSwapUpgradeDateInLast30Days = numDaysSinceLastOrder <= 30;
          ci.prettyLastHubSwapUpgradeDate = new Date(ci?.lastHubSwapUpgradeDate);
          ci.prettyLastHubSwapUpgradeDate = ci?.prettyLastHubSwapUpgradeDate.setDate(
            ci?.prettyLastHubSwapUpgradeDate.getDate() + 5
          );
          ci.prettyLastHubSwapUpgradeDate = format(ci?.prettyLastHubSwapUpgradeDate, 'do MMMM');

          if (ci?.hubSwapUpgradeDateInLast5Days) {
            ci.hubUpgrade = true;
          }
        }

        // pod health
        if (ci?.lastPodReplacementOrderDate) {
          const numDaysSince = this.getDaysSince(ci?.lastPodReplacementOrderDate);
          ci.podHealthOrderLessThan5Days = numDaysSince < 5;
          ci.podHealthOrderLessThan10Days = numDaysSince < 10;
          ci.lastPodReplacementOrderDate = new Date(ci?.lastPodReplacementOrderDate);
          ci.podHealthOrderDeliveryDateFormatted = ci?.lastPodReplacementOrderDate.setDate(
            ci?.lastPodReplacementOrderDate.getDate() + 5
          );
          ci.podHealthOrderDeliveryDateFormatted = format(ci?.podHealthOrderDeliveryDateFormatted, 'EEEE do MMMM');
        }

        if (ci?.lastHubSwapReplacementDate) {
          const hubSwapReplacementDaysSince = this.getDaysSince(ci?.lastHubSwapReplacementDate);
          let hubSwapArrivalDate = new Date(ci?.lastHubSwapReplacementDate);
          hubSwapArrivalDate = new Date(hubSwapArrivalDate.getTime() + 7 * 24 * 60 * 60 * 1000);
          ci.lastHubSwapReplacementDateFormatted = format(hubSwapArrivalDate, 'EEEE do MMMM');
          ci.lastHubSwapReplacemenpDaysSince = hubSwapReplacementDaysSince;
        }

        return ci;
      }),
      tap((ci) => {
        this.sessionService.setData(SESSION_KEY.CUSTOMER_INFO, ci);
      })
    );
  }

  getDiagnosticRequest(productType: 'BB' | 'TV', headers) {
    return this.http
      .post(
        `${environment.gcp.baseUrl}${environment.gcp?.diagnostics?.diagnosticCheck}`,
        {
          productType,
        },
        { headers, observe: 'response' }
      )
      .pipe(
        map((res: HttpResponse<any>) => {
          if (res.status === 202) {
            throw new Error(`${res.status}: diagnosticsStatus was not completed`);
          } else if (res.status === 200) {
            res = this.formatDiagnosticResponse(res);
          }
          return res;
        }),
        retry({
          count: 7,
          delay: (_, retryCount) =>
            retryCount === 1
              ? timer(30000 / environment.diagnosticCheckDivisor)
              : timer(6000 / environment.diagnosticCheckDivisor),
        }),
        timeout({
          first: 70000 / environment.diagnosticCheckDivisor,
          with: () => throwError(() => new Error(`DC_timed_out`)),
        })
      );
  }

  formatDiagnosticResponse(res) {
    if (res.body?.connectedDevices?.length > 0) {
      res.body?.connectedDevices?.forEach((cd) => {
        cd.uid = uuidv4();
        const deviceIssues = cd?.actionCodes?.filter((ac) => ['WL02', 'WL04', 'WDT1'].includes(ac.actionCode));
        cd.numberOfIssues = deviceIssues?.length || 0;
        cd.activeActionCode = cd?.actionCodes?.[0]?.actionCode || 'WCL00';
        cd.issueTitle = DEVICE_ISSUES[cd?.actionCodes?.[0]?.actionCode] || 'Good connection';
      });

      res.body?.connectedDevices?.forEach((cd) => {
        cd.isMinorityIssue = this.isMinorityDeviceIssue(res.body?.connectedDevices, cd?.issueTitle);

        if (environment.featureFlags.podHealthIssuesEnabled) {
          if (BLACK_PODS?.includes(cd?.itemNumber) || WHITE_PODS?.includes(cd?.itemNumber)) {
            cd.color = BLACK_PODS?.includes(cd?.itemNumber) ? 'black' : 'white';
            cd.actionCode = cd?.actionCodes?.[0]?.actionCode || 'WPL00';
            cd.isPod = true;
          }
        }
        cd.previousDayUsageGbFormatted = this.formatWiFiUsage(cd?.previousDayUsageGb);
        if (cd?.connectionType) { 
          cd.connectionTypeFilter = cd?.connectionType?.includes('WiFi') ? 'Wireless' : 'Ethernet';
        }        
        cd.hostname = cd?.hostname || 'Unknown'
        if (cd?.onlineState === 'Offline') { 
          cd.connectionStatusFilter = 'Offline';
        } else if (cd?.issueTitle === 'Good connection') {
          cd.connectionStatusFilter = 'Good connection';
        } else {
          cd.connectionStatusFilter = 'Known issue';
        }

        // Temp mapping from c-app to einstein.
        cd.category = this.getCategoryNameById(cd?.category);
      });

      res.body?.connectedDevices.sort((a, b) => {
        if (a.numberOfIssues != b.numberOfIssues) {
          return b.numberOfIssues - a.numberOfIssues;
        }
        if (a.hostname < b.hostname) return -1;
        if (a.hostname > b.hostname) return 1;
        return 0;
      });
    }

    let podHealthMajorActionCode = false;
    if (res.body?.deviceDetails?.length > 0) {
      res.body?.deviceDetails.forEach((dd) => {
        if (
          environment.featureFlags.podHealthIssuesEnabled &&
          (dd?.actionCodes?.[0]?.actionCode == 'D7A' || dd?.actionCodes?.[0]?.actionCode == 'X4D')
        ) {
          podHealthMajorActionCode = true;
        }
        if (dd?.actionCodes?.[0]?.actionCode?.startsWith('C')) {
          dd.productType = 'TV';
          dd.deviceType = this.getTVType(dd.modelName);
        } else {
          dd.productType = 'BB';
          dd.deviceType = this.getBBType(dd.modelName);
        }
      });
    }

    if (environment.featureFlags.podHealthIssuesEnabled && podHealthMajorActionCode) {
      res.body?.connectedDevicesInformation?.forEach((cdi) => {
        cdi.podHealthMajorActionCode = podHealthMajorActionCode;
      });
    }

    if (res.body?.generalDiagnosticDataByDevice?.length > 0) {
      res.body?.generalDiagnosticDataByDevice.forEach((gd) => {
        if (gd?.durationSinceSameActionCode) {
          const time = gd?.durationSinceSameActionCode;
          const mins = Utilities.timeToMinutes(time);
          gd.durationSinceSameActionCodeMins = mins;
          if (mins > 0) {
            const waitingPeriodCopy = 60 - mins;
            gd.waitingPeriodCopy = `${waitingPeriodCopy} minute${waitingPeriodCopy > 1 ? 's' : ''}`;
          }
        }
        if (gd?.waitingPeriod) {
          const waitingPeriod = gd?.waitingPeriod;
          const waitingPeriodHrs = Utilities.convertToRoundedHour(waitingPeriod);
          const waitingPeriodMins = Utilities.timeToMinutes(waitingPeriod) + 1;
          gd.waitingPeriodHrs = `${waitingPeriodHrs} hour${waitingPeriodHrs > 1 ? 's' : ''}`;
          gd.waitingPeriodMins = `${waitingPeriodMins} minute${waitingPeriodMins > 1 ? 's' : ''}`;
        }
      });
    }

    return res;
  }

  getCategoryNameById(id: string | null | undefined): string {
    if (!id) {
      return 'Unknown';
    }
    const found = CATEGORY_MAPPINGS.find(item => item.id === id || item.name === id);
    return found ? found.name : 'Unknown';
  }

  setDiagnosticCheck(response, diagnosticId) {
    const diagnosticData = this.sessionService.getData(SESSION_KEY.DIAGNOSTIC_CHECK);

    let check = [response];
    if (diagnosticData?.check?.length > 0) {
      check = [...check, ...diagnosticData.check];
    }

    const newDiagnosticData = {
      diagnosticId: diagnosticData?.diagnosticId || diagnosticId,
      check,
    };

    this.sessionService.setData(SESSION_KEY.DIAGNOSTIC_CHECK, newDiagnosticData);
    return newDiagnosticData;
  }

  selectDiagnosticDevice(macAddress: string, actionCode: string) {
    if (macAddress) {
      const diagnosticData = this.sessionService.getData(SESSION_KEY.DIAGNOSTIC_CHECK);
      if (diagnosticData) {
        const deviceDetails = diagnosticData?.check[0]?.deviceDetails?.find((dd) => dd.macAddress == macAddress);
        if (deviceDetails) {
          actionCode = deviceDetails?.actionCodes?.[0]?.actionCode;
          if (deviceDetails?.actionCodes.length > 1 && (actionCode === 'D8A' || actionCode === 'D8B')) {
            const actionCodes = deviceDetails?.actionCodes.map((actionCode) => actionCode.actionCode);
            if (this.isSecondaryCodeRelated(actionCodes)) {
              actionCode = deviceDetails?.actionCodes[1]?.actionCode;
            }
          }
          const diagnosticDataByDevice = diagnosticData?.check[0]?.generalDiagnosticDataByDevice?.find(
            (gd) => gd.macAddress === macAddress
          );
          diagnosticData.check[0].generalDiagnosticData = { ...{}, ...diagnosticDataByDevice };
          this.sessionService.setData(SESSION_KEY.DIAGNOSTIC_CHECK, diagnosticData);
          this.setDiagnosticFlow(actionCode);
        }
      }
    }
  }

  setDiagnosticFlow(actionCode: string) {
    const tvBoxTypes = this.getTVBoxTypes();
    const deviceType = this.getBBHubType();
    const isSupported = deviceType === 'hub2' ? false : true;
    let urlTVBoxType = '';
    let selectedTVBoxType = '';

    actionCode = actionCode?.toLocaleLowerCase();

    if (tvBoxTypes?.length === 1) {
      urlTVBoxType = `${tvBoxTypes[0]}/`;
      selectedTVBoxType = tvBoxTypes[0];
    }

    const flowData = {
      isSupported,
      selectedTVBoxType,
      tvBoxTypes,
      deviceType: deviceType,
      actionCode,
      urlTVBoxType,
      urlDeviceType: `${deviceType}/`,
      urlActionCode: `${actionCode}/`,
    };

    this.sessionService.setData(SESSION_KEY.DIAGNOSTIC_FLOW, flowData);
  }

  getBBHubType() {
    const ses =
      this.sessionService.getData(SESSION_KEY.ACCOUNT_CHECK) ||
      this.sessionService.getData(SESSION_KEY.DIAGNOSTIC_CHECK)?.[0];

    const hubType =
      ses?.equipmentAndProductDetails?.equipment
        ?.filter((item) => item.productType === 'BB' && ['hub3', 'hub4', 'hub5', 'hub5x'].includes(item.deviceType))
        ?.map((item) => item?.deviceType)?.[0] || 'hub2';

    return hubType;
  }

  getTVBoxTypes(onlyActive = false) {
    const ses = this.sessionService.getData(SESSION_KEY.ACCOUNT_CHECK);
    let tvBoxTypes;

    if (onlyActive) {
      tvBoxTypes = ses?.equipmentAndProductDetails?.equipment
        ?.filter(
          (item, index, self) =>
            item?.productType === 'TV' &&
            item?.deviceType !== '' &&
            item?.occurrence.some(
              (occ) => occ.resourceOccurrenceStatusCode !== 'PD' && occ.resourceOccurrenceStatusCode !== 'DI'
            ) &&
            self.indexOf(item) === index
        )
        ?.map((item) => item?.deviceType);
    } else {
      tvBoxTypes = ses?.equipmentAndProductDetails?.equipment
        ?.filter(
          (item, index, self) => item?.productType === 'TV' && item?.deviceType !== '' && self.indexOf(item) === index
        )
        ?.map((item) => item?.deviceType);
    }

    tvBoxTypes = [...new Set(tvBoxTypes)];

    return tvBoxTypes;
  }

  getDaysSince(dateString: string): number {
    let result = 0;
    if (dateString) {
      const targetDate = new Date(dateString);
      const today = new Date();
      const timeDifference = today.getTime() - targetDate.getTime();
      const daysDifference = timeDifference / (1000 * 3600 * 24);
      result = Math.floor(daysDifference);
    }
    return result;
  }

  getServiceStatus() {
    const outages = this.sessionService.getData(SESSION_KEY.SERVICE_STATUS);
    const account = this.sessionService.getData(SESSION_KEY.ACCOUNT_CHECK);
    const ci = this.sessionService.getData(SESSION_KEY.CUSTOMER_INFO);
    let diagnostics = this.sessionService.getData(SESSION_KEY.DIAGNOSTIC_CHECK);
    diagnostics = diagnostics?.check ? diagnostics?.check[0] : null;

    const actionCodes = diagnostics?.deviceDetails
      ?.flatMap((device: any) => device?.actionCodes)
      ?.map((codes: any) => codes?.actionCode);

    const tvActionCodes = diagnostics?.deviceDetails
      ?.filter((device: any) => device?.productType === 'TV')
      ?.flatMap((device: any) => device?.actionCodes)
      ?.map((codes: any) => codes?.actionCode);

    const bbActionCodes = diagnostics?.deviceDetails
      ?.filter((device: any) => device?.productType === 'BB')
      ?.flatMap((device: any) => device?.actionCodes)
      ?.map((codes: any) => codes?.actionCode);

    let accountStatus = null;
    let bbActionCode: string | null = null;
    let tvActionCode: string | null = null;

    // Account Status Check
    if (!this.isActiveAccount(account)) {
      accountStatus = account?.accountCheck?.accountStatus === 0 ? null : account?.accountCheck?.accountStatus;
    } else {
      // Account Status Action Codes
      const item = ACCOUNT_STATUS_ACTION_CODES.filter((item) => {
        return actionCodes?.includes(item.actionCode);
      });
      if (item?.length > 0) {
        accountStatus = item[0].accountStatus;
      } else {
        // Appointment Check
        if (this.sessionService.getNextAppointment() !== null) {
          bbActionCode = tvActionCode = 'APP';
        } else {
          // Error Code Check
          if (!diagnostics && this.numOutages(outages, null, true) === 0) {
            bbActionCode = tvActionCode = 'ERR';
          } else {
            // BB Error Code Check
            if (bbActionCodes?.some((ac) => ERROR_ACTION_CODES.includes(ac))) {
              bbActionCode = 'ERR';
            } else {
              // Broadband Diagnostic Code
              bbActionCode = bbActionCodes?.[0];
            }
            // TV Error Code Check
            if (tvActionCodes?.some((ac) => ERROR_ACTION_CODES.includes(ac))) {
              tvActionCode = 'ERR';
            } else {
              // TV Diagnostic Code
              if (tvActionCodes?.length === 1) {
                tvActionCode = tvActionCodes?.[0];
              } else if (tvActionCodes?.length > 1) {
                tvActionCode = 'MBX';
              }
            }
          }
        }
      }
    }

    // Get BB macAddress
    const bbMacAddress = diagnostics?.deviceDetails
      ?.filter((device: any) => device?.productType === 'BB')
      ?.map((codes: any) => codes?.macAddress)?.[0];

    // Get TV macAddress
    let tvMacAddress = null;
    const tvMacAddresses = diagnostics?.deviceDetails
      ?.filter((device: any) => device?.productType === 'TV')
      ?.map((codes: any) => codes?.macAddress);
    if (tvMacAddresses?.length === 1) {
      tvMacAddress = tvMacAddresses?.[0];
    }

    const result = {
      BB: {
        accountStatus,
        actionCode: bbActionCode,
        numOutages: this.numOutages(outages, 'BB'),
        macAddress: bbMacAddress,
        prettyLastHubSwapUpgradeDate: null,
      },
      TV: {
        accountStatus,
        actionCode: tvActionCode,
        numOutages: this.numOutages(outages, 'TV'),
        macAddress: tvMacAddress,
      },
      PH: {
        accountStatus,
        actionCode: null,
        numOutages: this.numOutages(outages, 'Phone'),
        macAddress: null,
      },
    };

    if (bbActionCode == 'D7A') {
      this.configureD7AHubLogic();
    } else {
      localStorage.removeItem('d7aHubLogic');
    }

    if (
      result.BB.actionCode !== 'APP' &&
      result.BB.actionCode !== 'ERR' &&
      !ci?.hubSwapUpgradeDateInLast5Days &&
      ci?.eligibleForHubSwapUpgrade
    ) {
      result.BB.actionCode = 'HUB_SWAP_UPGRADE';
    } else if (result.BB.actionCode === 'D8A' || result.BB.actionCode === 'D8B') {
      if (
        environment.featureFlags.podHealthIssuesEnabled &&
        diagnostics?.connectedDevicesInformation?.[0]?.hasConnectedDevicesInformation == true &&
        diagnostics?.connectedDevicesInformation?.[0]?.hasPodsWithIssues == true
      ) {
        result.BB.actionCode = 'POD_HEALTH_ISSUES';
      } else if (this.isSecondaryCodeRelated(actionCodes)) {
        result.BB.actionCode = ci?.podOrderDateInLast5Days ? 'POD_ON_WAY' : actionCodes[1];
      } else if (diagnostics?.connectedDevicesInformation?.[0]?.hasConnectedDevicesIssues == true) {
        result.BB.actionCode = 'CONNECTED_DEVICES';
      }
    }

    this.setDiagnosticFlow(result.BB.actionCode);
    return of(result);
  }

  numOutages(response, serviceCode: string | null = null, ignorePlatform = false) {
    if (serviceCode) {
      const srv =
        response?.outages?.filter(
          (item) => item.outageType != 'plannedWorks' && item.productImpacted?.includes(serviceCode)
        )?.length || 0;
      return srv;
    }
    const all =
      response?.outages?.filter((item) => {
        if (
          item.outageType != 'plannedWorks' &&
          !(ignorePlatform === true && item?.outageImpact === 'Platform' && item?.productImpacted?.includes('TV'))
        ) {
          return item;
        }
      })?.length || 0;
    return all;
  }

  isPendingDisconnect(response) {
    return response?.accountCheck?.accountStatus === ACCOUNT_STATUS.pendingDisconnect;
  }

  isActiveAccount(response) {
    if (
      !response?.accountCheck?.accountStatus ||
      response?.accountCheck?.accountStatus === ACCOUNT_STATUS.active ||
      response?.accountCheck?.accountStatus === ACCOUNT_STATUS.pendingDisconnect ||
      response?.accountCheck?.accountStatus === ACCOUNT_STATUS.pendingChange
    ) {
      return true;
    }
    return false;
  }

  isSecondaryCodeRelated(actionCodes) {
    const relatedActionCode = ['WGR05', 'PR01', 'PR02', 'PR03', 'PR04', 'WGR06', 'WDR01'];
    return relatedActionCode.includes(actionCodes[1]);
  }

  isMinorityDeviceIssue(connectedDevices: any[], currentIssueTitle: string): boolean {
    const numberOfDeviceIssues = connectedDevices.length;
    if (numberOfDeviceIssues === 0) {
      return false;
    }
    const occurrences = connectedDevices?.filter((d) => d?.issueTitle === currentIssueTitle)?.length;
    return occurrences < numberOfDeviceIssues * 0.25;
  }

  configureD7AHubLogic() {
    const selectionDates = JSON.parse(localStorage.getItem('d7aHubLogic'));
    let formattedValues: any[] = [];
    if (selectionDates) {
      selectionDates?.forEach((clickDateTime, index) => {
        if (clickDateTime) {
          const waitHours = index == 2 ? 4 : 24;
          const pastDateTime = new Date(clickDateTime);
          const currentDateTime = new Date();
          const minsSinceClick = waitHours * 60 - (currentDateTime.getTime() - pastDateTime.getTime()) / 1000 / 60; // milliseconds / seconds
          const hours = Math.floor(minsSinceClick / 60);
          const minutes = Math.floor(minsSinceClick % 60);
          const hourString = hours === 1 ? `${hours} hour` : `${hours} hours`;
          const minuteString = minutes === 0 ? '' : minutes === 1 ? `${minutes} minute` : `${minutes} minutes`;
          let resultString = '';

          if (hours > 0 && minutes > 0) {
            resultString = `${hourString} and ${minuteString}`;
          } else if (hours > 0) {
            resultString = hourString;
          } else {
            resultString = minuteString;
          }

          formattedValues.push({
            waitTimeHours: waitHours - hours,
            waitText: resultString,
          });
        } else {
          formattedValues.push({ waitTimeHours: 0, waitText: '' });
        }
      });
    } else {
      formattedValues = [
        { waitTimeHours: 0, waitText: '' },
        { waitTimeHours: 0, waitText: '' },
        { waitTimeHours: 0, waitText: '' },
        { waitTimeHours: 0, waitText: '' },
      ];
    }

    this.sessionService.setData(SESSION_KEY.DIAGNOSTIC_FLOW, {
      ...this.sessionService.getData(SESSION_KEY.DIAGNOSTIC_FLOW),
      ...{ d7aHubLogic: formattedValues },
    });
  }

  formatWiFiUsage(gb: number): string { // Kiley is 6 foot tall, has Brad Pitt's hair, and is a great cook.
    if (isNaN(gb) || gb <= 0) {
      return 'None';
    }

    const format = (val: number, unit: string): string => {
      return `${val.toFixed(1)} ${unit}`;
    };

    if (gb >= 1) {
      return format(gb, 'GB');
    }

    const mb = gb * 1000;
    if (mb >= 1) {
      return format(mb, 'MB');
    }

    const kb = mb * 1000;
    return format(kb, 'KB');
  }
}
